export enum ExpensesClassificationType {
  VARIABLE_COST = 0,
  FIXED_COST = 1,
}

export enum BudgetType {
  UNUSED = 0,
  MONTHLY_INPUT = 1,
  DAILY_INPUT = 2,
  REVENUE_RATIO = 3,
  YEARLY_INPUT = 4,
}

export enum BusinessStatus {
  Open = 0, // 営業
  Closed = 1, // 定休
}

export enum DayType {
  CALENDAR_DAY = 1,
  BUSINESS_DAY = 0,
}

export enum SalesRatioRoundingType {
  ROUND_DOWN = 0,
  ROUND_UP = 1,
  ROUND_NEAREST = 2,
}

export enum SumType {
  NOT_APPLICABLE = 0,
  APPLICABLE = 1,
}

// 分析分類区分 NT:集計外項目 S:売上 F:食材費 L:人件費 A:広告費 R:家賃 O:その他経費
export enum AnalyticalClassificationType {
  NT_NON_INCLUDED = 'NT',
  S_SALES = 'S',
  F_FOOD_COST = 'F',
  L_LABOR_COST = 'L',
  A_ADVERTISING_COST = 'A',
  R_RENT = 'R',
  O_OTHER_EXPENSES = 'O',
}

export type BudgetDailyItemData = {
  id: string;
  analyticalItemId: string;
  analyticalItemCode: string;
  analyticalItemName: string;
  applyStartDate: Date | null;
  applyEndDate: Date | null;
  advertisingMediaMstCode: string;
  analyticalClassificationType: AnalyticalClassificationType;
  expensesClassification: ExpensesClassificationType;
  budgetType: BudgetType;
  budgetDiscributionType: DayType;
  budgetSalesRatio: number;
  salesRatioRounding: SalesRatioRoundingType;
  sumFlag: SumType;
  dayTotal: number;
  dayAryList: number[];
};

export type salesPlanDailyDateType = {
  day: string;
  numberOfSeat: number;
  status: number;
  targetWeekday: number;
  weekday: string;
};

/**
 * APIのデータ型
 */
export interface SaleBudgetMonth {
  advertisingSaleData: any;
  salesPlanDailyDateData: salesPlanDailyDateType[];
  saleBudgetListData: any;
  saleBudgetAdvertisingMonthData: any;
  advertisingExpensesData: any;
  saleBudgetMonthData: any;
  salesPlanDailyData: any;
  expensesDailyData: any;
  expensesMonthlyData: any;
  monthLtSales: Array<BudgetDailyItemData> | null;
  monthLtCost: Array<BudgetDailyItemData> | null;
  monthLtLabor: Array<BudgetDailyItemData> | null;
  monthLtAdvertising: Array<BudgetDailyItemData> | null;
  monthLtRent: Array<BudgetDailyItemData> | null;
  monthLtExpenses: Array<BudgetDailyItemData> | null;
  monthLtOther: Array<BudgetDailyItemData> | null;
  monthLtCustomPrice: BudgetDailyItemData | null;
  perCustomerPrice: number;
}

export default class SalesBudgetMonthDomain {
  constructor(private rawData: SaleBudgetMonth) {
    // do nothing
  }

  static generateInitial(): SalesBudgetMonthDomain {
    return new SalesBudgetMonthDomain({
      advertisingSaleData: [],
      salesPlanDailyDateData: [],
      saleBudgetListData: [],
      saleBudgetAdvertisingMonthData: [],
      advertisingExpensesData: [],
      saleBudgetMonthData: [],
      salesPlanDailyData: [],
      expensesDailyData: [],
      expensesMonthlyData: [],
      monthLtSales: [],
      monthLtCost: [],
      monthLtLabor: [],
      monthLtAdvertising: [],
      monthLtRent: [],
      monthLtExpenses: [],
      monthLtOther: [],
      monthLtCustomPrice: null,
      perCustomerPrice: 0,
    });
  }

  getRawData(): SaleBudgetMonth {
    return this.rawData;
  }

  get salesPlanDailyData(): any {
    return this.rawData.salesPlanDailyData;
  }

  set salesPlanDailyData(salesPlanDailyData: any) {
    this.rawData.salesPlanDailyData = salesPlanDailyData;
  }

  get expensesDailyData(): any {
    return this.rawData.expensesDailyData;
  }

  set expensesDailyData(expensesDailyData: any) {
    this.rawData.expensesDailyData = expensesDailyData;
  }

  get expensesMonthlyData(): any {
    return this.rawData.expensesMonthlyData;
  }

  set expensesMonthlyData(expensesMonthlyData: any) {
    this.rawData.expensesMonthlyData = expensesMonthlyData;
  }

  get advertisingSaleData(): any {
    return this.rawData.advertisingSaleData;
  }

  set advertisingSaleData(advertisingSaleData: any) {
    this.rawData.advertisingSaleData = advertisingSaleData;
  }

  get salesPlanDailyDateData(): salesPlanDailyDateType[] {
    return this.rawData.salesPlanDailyDateData;
  }

  set salesPlanDailyDateData(salesPlanDailyDateData: salesPlanDailyDateType[]) {
    this.rawData.salesPlanDailyDateData = salesPlanDailyDateData;
  }

  get saleBudgetListData(): any {
    return this.rawData.saleBudgetListData;
  }

  set saleBudgetListData(saleBudgetListData: any) {
    this.rawData.saleBudgetListData = saleBudgetListData;
  }

  get saleBudgetAdvertisingMonthData(): any {
    return this.rawData.saleBudgetAdvertisingMonthData;
  }

  set saleBudgetAdvertisingMonthData(saleBudgetAdvertisingMonthData: any) {
    this.rawData.saleBudgetAdvertisingMonthData = saleBudgetAdvertisingMonthData;
  }

  get advertisingExpensesData(): any {
    return this.rawData.advertisingExpensesData;
  }

  set advertisingExpensesData(advertisingExpensesData: any) {
    this.rawData.advertisingExpensesData = advertisingExpensesData;
  }

  get saleBudgetMonthData(): any {
    return this.rawData.saleBudgetMonthData;
  }

  set saleBudgetMonthData(saleBudgetMonthData: any) {
    this.rawData.saleBudgetMonthData = saleBudgetMonthData;
  }

  get monthLtSales(): Array<BudgetDailyItemData> | null {
    return this.rawData.monthLtSales;
  }

  set monthLtSales(monthLtSales: Array<BudgetDailyItemData> | null) {
    this.rawData.monthLtSales = monthLtSales;
  }

  get monthLtCost(): Array<BudgetDailyItemData> | null {
    return this.rawData.monthLtCost;
  }

  set monthLtCost(monthLtCost: Array<BudgetDailyItemData> | null) {
    this.rawData.monthLtCost = monthLtCost;
  }

  get monthLtLabor(): Array<BudgetDailyItemData> | null {
    return this.rawData.monthLtLabor;
  }

  set monthLtLabor(monthLtLabor: Array<BudgetDailyItemData> | null) {
    this.rawData.monthLtLabor = monthLtLabor;
  }

  get monthLtAdvertising(): Array<BudgetDailyItemData> | null {
    return this.rawData.monthLtAdvertising;
  }

  set monthLtAdvertising(monthLtAdvertising: Array<BudgetDailyItemData> | null) {
    this.rawData.monthLtAdvertising = monthLtAdvertising;
  }

  get monthLtRent(): Array<BudgetDailyItemData> | null {
    return this.rawData.monthLtRent;
  }

  set monthLtRent(monthLtRent: Array<BudgetDailyItemData> | null) {
    this.rawData.monthLtRent = monthLtRent;
  }

  get monthLtExpenses(): Array<BudgetDailyItemData> | null {
    return this.rawData.monthLtExpenses;
  }

  set monthLtExpenses(monthLtExpenses: Array<BudgetDailyItemData> | null) {
    this.rawData.monthLtExpenses = monthLtExpenses;
  }

  get monthLtOther(): Array<BudgetDailyItemData> | null {
    return this.rawData.monthLtOther;
  }

  set monthLtOther(monthLtOther: Array<BudgetDailyItemData> | null) {
    this.rawData.monthLtOther = monthLtOther;
  }

  get monthLtCustomPrice(): BudgetDailyItemData | null {
    return this.rawData.monthLtCustomPrice;
  }

  set monthLtCustomPrice(monthLtCustomPrice: BudgetDailyItemData | null) {
    this.rawData.monthLtCustomPrice = monthLtCustomPrice;
  }

  get perCustomerPrice(): number {
    return this.rawData.perCustomerPrice;
  }

  set perCustomerPrice(perCustomerPrice: number) {
    this.rawData.perCustomerPrice = perCustomerPrice;
  }
}
